import type { DiscountCapResource } from '@repo/api-client';

import type { PricedBasket } from './tax-invoice.ts';

/**
 * The most a cashier may take off a bill of `billPaise`, under the park's
 * discount cap (#352, blossom-rest-api#552).
 *
 * The API's rule, as it states it: **the lower of the two bounds** against the
 * bill total; **a null bound does not apply**; both null is no cap. And never
 * more than the bill itself, which the server holds a sale to regardless.
 *
 * The server does not refuse a sale over the cap — a till holding a stale copy
 * would otherwise lose a sale after the cash is in the drawer — so this is the
 * only refusal a guest ever sees. Here in the bridge because the till enforces
 * it and the back office shows the owner the same figure as they type.
 *
 * The percentage is rounded **down** to the paisa: the cap is a ceiling, and
 * rounding up would allow a paisa beyond what the owner set. Worked in `bigint`
 * so a large bill is never a rounded double.
 */
export function mostOffBill(cap: DiscountCapResource, billPaise: number): number {
  let most = BigInt(billPaise);

  if (cap.maxBasisPoints !== null) {
    const share = (BigInt(billPaise) * BigInt(cap.maxBasisPoints)) / 10_000n;
    if (share < most) most = share;
  }

  if (cap.maxPaise !== null && BigInt(cap.maxPaise) < most) most = BigInt(cap.maxPaise);

  return Number(most);
}

/**
 * The most a cashier may take off a basket priced with no discount: the cap
 * against **what the guest would have paid** — so ₹25 off a ₹250 souvenir whose
 * taxed line is 249.99 and whose invoice is 250.00 — and never more than the
 * lines' sum, which is all the server lets a discount take.
 */
export function mostOffBasket(cap: DiscountCapResource, undiscounted: PricedBasket): number {
  const lines = undiscounted.lines.reduce((sum, line) => sum + line.amountPaise, 0);

  return Math.min(mostOffBill(cap, undiscounted.totalPaise), lines);
}
